import React from 'react';
import Box from "@mui/material/Box";
import {Chip, CircularProgress, Typography} from "@mui/joy";
import {Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import moment from "moment";
import AssetProductImage from "../AssetProductImage";
import {AssetProposalConfidence} from "../../codegen/graphql";
import {CatalogProductNode} from "@dreebit-registry/vsm-microservice-components";

/**
 * The proposal branch of a serialNumberLookup result (confidence EXACT or
 * NEEDS_CONFIRMATION): nothing is registered yet, the product node is only a
 * catalog suggestion derived from the systems of record.
 */
export interface SerialLookupProposal {
    confidence: AssetProposalConfidence;
    /** Canonical serial number as resolved by the systems of record — may differ
     *  from what the user typed (e.g. leading zeros stripped). This spelling is
     *  what gets registered. */
    serialNumber: string;
    dateOfManufacture?: string | null;
    manufacturer?: { id: string; name?: string | null } | null;
    proposedProductNode: CatalogProductNode;
}

interface Props {
    proposal: SerialLookupProposal;
    onClick: () => void;
    /** EXACT direct registration in flight — blocks further clicks. */
    loading?: boolean;
}

/**
 * Card for an unregistered serial number the lookup could match to a catalog
 * product. Mirrors the AssetSearchResult card layout, plus a "Suggestion" chip
 * and a hint line telling the user what a click does: EXACT registers the
 * asset directly, NEEDS_CONFIRMATION opens the product browser preselected.
 */
const ProposalSearchResult = ({proposal, onClick, loading}: Props) => {

    const {t} = useTranslation();
    const product = proposal.proposedProductNode;

    const detailInfo = [
        {
            label: 'Serial no.',
            value: proposal.serialNumber
        }, {
            label: 'Manufacturer',
            value: product.manufacturer?.name ?? proposal.manufacturer?.name
        }, {
            label: 'Product code',
            value: product.productCode
        }, {
            label: 'Date of manufacture',
            value: proposal.dateOfManufacture ? moment(proposal.dateOfManufacture).format("L") : undefined
        }
    ];

    // Only the EXACT card carries a hint: its click registers immediately, which
    // the user must know beforehand. NEEDS_CONFIRMATION just navigates.
    const hint = proposal.confidence === AssetProposalConfidence.EXACT
        ? t('Not yet registered — select to register this asset.')
        : null;

    return <Box className={'flex-column'}
                data-testid={'serial-lookup-proposal'}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    flexShrink: 0,
                    border: '1px solid #ccc',
                    padding: '13px',
                    borderRadius: '4px',
                    gap: '8px',
                    cursor: loading ? 'default' : 'pointer',
                    boxSizing: 'border-box',
                    opacity: loading ? 0.6 : 1,
                }}
                onClick={loading ? undefined : onClick}
    >
        <Box className={'flex-row'} sx={{gap: '16px', minHeight: 106}}>
            <Box sx={{padding: '8px', display: 'flex', alignItems: 'center'}}>
                {loading
                    ? <CircularProgress size={'md'}/>
                    : <AssetProductImage style={{height: 90, width: 'auto'}}
                                         src={product.image?.thumbnailLink ?? undefined}/>
                }
            </Box>
            <Box className={'flex-col'} sx={{flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}} alignContent="center">
                <Box className={'flex-row'} sx={{alignItems: 'center', gap: '8px', minWidth: 0}}>
                    <Tooltip title={product.name} enterDelay={750}>
                        <Typography level={'titleLargeBold'} noWrap>{product.name}</Typography>
                    </Tooltip>
                    <Chip size={'sm'} variant={'soft'} color={'primary'} sx={{flexShrink: 0}}>
                        {t('Suggestion')}
                    </Chip>
                </Box>
                <Box display={'flex'} flexDirection={'column'} gap={'4px'} sx={{pl: '2px'}}>
                    {detailInfo.map((detail) => {
                        if (detail.value) {
                            return <Box className={'flex-row'} minWidth={'180px'} gap='5px' key={detail.label}>
                                <Typography level={'bodySmallLight'}>{t(detail.label)}:</Typography>
                                <Typography level={'bodySmallBold'}>{detail.value}</Typography>
                            </Box>
                        }
                        return null;
                    })}
                </Box>
            </Box>
        </Box>
        {hint && <Typography level={'bodySmall'} sx={{pl: '2px'}}>{hint}</Typography>}
    </Box>;
};

export default ProposalSearchResult;
